import type { IndicatorSelection } from "components/financials/modern/types";
import getYear from "date-fns/getYear";
import sum from "lodash/sum";

export default function derivedValuesFromSelection(selection: IndicatorSelection[]) {
  const nodes = selection
    .filter(({ value }) => {
      return (
        value.__typename !== "FinancialIndicatorSeriesTextNode" &&
        value.__typename !== "FinancialIndicatorSeriesSkeletonNode"
      );
    })
    .map(({ value, reportDate }) => {
      if (
        value.__typename === "FinancialIndicatorSeriesTextNode" ||
        value.__typename === "FinancialIndicatorSeriesSkeletonNode"
      ) {
        return {
          reportDate,
          value: 0,
        };
      }
      return {
        reportDate,
        value: value.value,
      };
    });
  const values = nodes.map(({ value }) => value);

  if (nodes.length < 2) {
    return {
      type: selection[0].value.__typename,
      values,
      variation: 0,
      annualReturn: 0,
      totalSum: 0,
      firstValue: 0,
      lastValue: 0,
      minValue: 0,
      maxValue: 0,
      avgValue: 0,
      valuesAreTheSame: true,
      displayValue: 0,
    };
  }

  const years = getYear(nodes[nodes.length - 1].reportDate) - getYear(nodes[0].reportDate);
  const totalSum = sum(values);
  let firstValue = values[0];
  const lastValue = values[values.length - 1];
  const valuesAreTheSame = values.every((value) => value === firstValue);

  if (values.filter((value) => value > 0).length === 1 && lastValue > 0) {
    return {
      type: selection[0].value.__typename,
      values,
      variation: 0,
      annualReturn: 0,
      totalSum,
      firstValue,
      lastValue,
      displayValue:
        (firstValue < 0 && lastValue > 0) || (firstValue > 0 && lastValue < 0)
          ? firstValue + lastValue
          : lastValue - firstValue,
      minValue: 0,
      maxValue: lastValue,
      avgValue: 0,
      valuesAreTheSame,
    };
  }

  if (firstValue === 0 && lastValue !== 0) {
    firstValue = values.find((value) => value !== 0) || 0;
  }

  const allValuesAreNegative = values.every((value) => value < 0);
  let variation = totalSum === 0 ? 0 : (lastValue - firstValue) / firstValue;

  if (!variation || !Number.isFinite(variation)) {
    variation = 0;
  }

  if ((firstValue < 0 && lastValue > 0) || (firstValue > 0 && lastValue < 0)) {
    variation = 0;
  }

  if (allValuesAreNegative && totalSum < 0) {
    variation *= -1;
  }

  const minValue = values.length === 0 ? 0 : Math.min(...values);
  const maxValue = values.length === 0 ? 0 : Math.max(...values);
  const avgValue = values.length === 0 ? 0 : totalSum / values.length;

  const annualReturn = years >= 2 ? (1 + variation) ** (1 / years) - 1 : null;

  return {
    type: selection[0].value.__typename,
    values,
    variation,
    annualReturn,
    totalSum,
    firstValue,
    lastValue,
    minValue,
    maxValue,
    avgValue,
    valuesAreTheSame,
    displayValue: lastValue - firstValue,
  };
}
